import React, {useEffect, useState} from "react";
import {Grid, makeStyles, Paper} from "@material-ui/core";
import messagingService from "api/messaging";
import {useOrganiser, useUserConversations} from "hooks";
import {StandsWithCompanies} from "ts/api/chat";
import {defaultLocalMap} from "tools/show/localMap";
import NavBar from "../NavBar";
import Conversation from "../Conversation";

const nameNotFound = "NAME NOT FOUND";

type ChatProps = {
  showId: string;
  userRole: string;
  userId: string;
};

const CHAT_NAVBAR_WIDTH = 298;

const Chat = ({showId, userId, userRole}: ChatProps) => {
  const classes = useStyles();
  const [selectedConversationId, setSelectedConversationId] = useState("");

  const {conversations, loading} = useUserConversations(showId, "organisers");
  const [creatingNewChat, setCreatingNewChat] = useState(false);
  const [conversationWasChanged, setConversationWasChanged] = useState(false);
  const [organiser] = useOrganiser();
  const [fullName, setFullName] = useState<string>(nameNotFound);

  useEffect(() => {
    setFullName(organiser?.contactPerson || nameNotFound);
  }, [organiser, setFullName]);
  const selectConversation = (id: string) => {
    setConversationWasChanged(true);
    setSelectedConversationId(id);
    setCreatingNewChat(false);
    setTimeout(() => {
      setConversationWasChanged(false);
    }, 500);
  };

  const createChat = () => {
    setSelectedConversationId("");
    setCreatingNewChat(true);
  };

  const createConversation = async (item: StandsWithCompanies) => {
    let name = `(${item.stand.name}) ${item.allCompaniesNames}`;

    if (item?.stand?.suffixName) {
      name = `(${item.stand.name} (${item?.stand?.suffixName})) ${item.allCompaniesNames}`;
    }

    const data = {
      id: item.stand.standId,
      name,
      logo:
        item.pairs.find(
          (p) => p?.companyToShow?.company?.companyInfo?.logoFile?.url
        )?.companyToShow?.company?.companyInfo?.logoFile?.url ?? "",
    };

    await messagingService.createOrUpdateStandToOrganisersChat(
      showId,
      item,
      data,
      "organiser"
    );
    setSelectedConversationId(item.stand.standId);
    setCreatingNewChat(false);
  };

  const getSelectedConversation = () => {
    if (!selectedConversationId) {
      return null;
    }
    return conversations?.organisers?.[selectedConversationId];
  };

  return (
    <Grid container spacing={2} className={classes.container}>
      <Grid item className={classes.navbarGrid}>
        <Paper className={classes.navbarPaper}>
          <NavBar
            showId={showId}
            userId={userId}
            userRole={userRole}
            conversations={conversations}
            isLoading={loading}
            selectConversation={selectConversation}
            createChat={createChat}
            selectedConversationId={selectedConversationId}
          />
        </Paper>
      </Grid>

      <Grid item xs className={classes.chatGrid}>
        <Paper className={classes.chatPaper}>
          <Conversation
            localizedOrganiserLabel={defaultLocalMap.organiser}
            showId={showId}
            userId={userId}
            userRole={userRole}
            organiserId={userId}
            standId={selectedConversationId}
            localizedExhibitorLabel={defaultLocalMap.exhibitor}
            selectedConversation={selectedConversationId}
            createConversation={createConversation}
            conversationData={getSelectedConversation()}
            fullName={fullName}
            hasConversations={Boolean(conversations)}
            creatingNewChat={creatingNewChat}
            conversationWasChanged={conversationWasChanged}
          />
        </Paper>
      </Grid>
    </Grid>
  );
};

const useStyles = makeStyles(({spacing}) => ({
  container: {
    height: "100%",
    width: "100%",
    flexWrap: "nowrap",
    overflow: "auto",
  },
  navbarGrid: {width: CHAT_NAVBAR_WIDTH, height: "100%"},
  navbarPaper: {height: "100%", width: "100%", padding: spacing(3, 0)},
  chatGrid: {
    height: "100%",
    width: `calc(100% - ${CHAT_NAVBAR_WIDTH}px)`,
    minWidth: 420,
    flexGrow: 1,
  },
  chatPaper: {height: "100%", width: "100%", padding: 0},
}));

export default Chat;
